import m from 'mithril';
import { Store } from '../store';
import { currentLang } from '../i18n';
import { Product } from '../store';

interface LiveSearchState {
    isFocused: boolean;
}

export const LiveSearch: m.Component<{}, LiveSearchState> = {
    // Local state for the dropdown
    oninit(vnode) {
        vnode.state.isFocused = false;
    },

    view(vnode) {
        const state = vnode.state;
        const query = Store.state.searchQuery.trim().toLowerCase();
        
        // Filter products if there's a query
        let results: Product[] = [];
        if (query.length > 0) {
            results = Store.state.products.filter(p => 
                p.name.toLowerCase().includes(query) || 
                (p.description && p.description.toLowerCase().includes(query))
            ).slice(0, 5); // Max 5 results for the dropdown
        }

        const showDropdown = state.isFocused && query.length > 0;

        return m('div', { class: 'hidden md:flex flex-1 max-w-md mx-4 relative group' }, [
            // Background glow effect
            m('div', { class: 'absolute inset-0 bg-gradient-to-r from-[var(--color-primary)] to-[var(--color-secondary)] rounded-full blur opacity-0 group-focus-within:opacity-20 transition-opacity duration-500' }),
            
            // Search Input Pill
            m('div', { class: 'relative w-full flex items-center bg-[var(--color-bg-base)] border border-[var(--color-border)]/50 rounded-full shadow-inner overflow-hidden z-[51]' }, [
                m('iconify-icon', { icon: 'lucide:search', class: 'ms-3 -me-1 text-[var(--color-text-base)] opacity-40 group-focus-within:opacity-100 group-focus-within:text-[var(--color-primary)] transition-colors' }),
                m('input', {
                    type: 'text',
                    placeholder: currentLang === 'en' ? 'Search anything...' : 'ابحث عن أي شيء...',
                    value: Store.state.searchQuery,
                    oninput: (e: any) => Store.handleSearch(e.target.value),
                    onfocus: () => { state.isFocused = true; },
                    onblur: () => {
                        // Delay blurring so clicks on dropdown items can register before it disappears
                        setTimeout(() => {
                            state.isFocused = false;
                            m.redraw();
                        }, 200);
                    },
                    class: 'w-full ps-3 pe-4 py-1.5 bg-transparent text-sm text-[var(--color-text-base)] placeholder-[var(--color-text-base)] placeholder-opacity-40 outline-none'
                }),
                // Clear button when there is text
                query.length > 0 && m('button', {
                    class: 'absolute end-3 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center rounded-full hover:bg-[var(--color-border)]/50 text-[var(--color-text-base)] opacity-50 hover:opacity-100 transition-all cursor-pointer bg-transparent border-0',
                    onclick: () => {
                        Store.handleSearch('');
                        (document.querySelector('input[type="text"]') as HTMLInputElement)?.focus();
                    }
                }, m('iconify-icon', { icon: 'lucide:x', class: 'text-sm' }))
            ]),

            // Instant Dropdown Results
            showDropdown && m('div', { 
                class: 'absolute top-full inset-inline-0 mt-2 bg-[var(--color-surface)] backdrop-blur-3xl border border-[var(--color-border)]/50 rounded-xl shadow-[var(--shadow-premium)] overflow-hidden z-[50] animate-fade-in-up' 
            }, [
                results.length > 0 ? [
                    m('div', { class: 'p-2 space-y-1' }, 
                        results.map(product => 
                            m('div', { 
                                class: 'flex items-center gap-3 p-2 hover:bg-[var(--color-border)]/20 rounded-lg cursor-pointer transition-colors group/item',
                                onclick: () => {
                                    state.isFocused = false;
                                    Store.handleSearch(''); // Optional: clear search on navigation
                                    m.route.set(`/product/${product.id}`);
                                }
                            }, [
                                // Thumbnail (Placeholder since we don't have true images mapped yet)
                                m('div', { class: 'w-10 h-10 rounded bg-[var(--color-bg-base)] border border-[var(--color-border)]/30 shrink-0 flex items-center justify-center' }, [
                                    m('iconify-icon', { icon: 'lucide:image', class: 'text-[var(--color-text-base)] opacity-50' })
                                ]),
                                // Info
                                m('div', { class: 'flex-1 overflow-hidden' }, [
                                    m('h4', { class: 'text-sm font-bold text-[var(--color-text-base)] truncate group-hover/item:text-[var(--color-primary)] transition-colors' }, product.name),
                                    m('p', { class: 'text-xs text-[var(--color-text-base)] opacity-60 truncate' }, product.description || 'No description')
                                ]),
                                // Price
                                m('div', { class: 'font-black text-[var(--color-primary)] text-sm shrink-0' }, `$${parseFloat(product.price).toFixed(2)}`)
                            ])
                        )
                    ),
                    m('button', { 
                        class: 'w-full py-2.5 text-xs font-bold uppercase tracking-wider text-center border-t border-[var(--color-border)]/30 hover:bg-[var(--color-primary)]/10 text-[var(--color-primary)] transition-colors cursor-pointer bg-transparent',
                        onclick: () => {
                            state.isFocused = false;
                            m.route.set('/shop');
                        }
                    }, currentLang === 'en' ? `See all results for "${Store.state.searchQuery}"` : `عرض جميع النتائج لـ "${Store.state.searchQuery}"`)
                ] : [
                    // Empty State inside dropdown
                    m('div', { class: 'p-6 text-center' }, [
                        m('iconify-icon', { icon: 'lucide:search-x', class: 'text-3xl text-[var(--color-text-base)] opacity-30 mb-2' }),
                        m('p', { class: 'text-sm text-[var(--color-text-base)] opacity-70' }, currentLang === 'en' ? 'No products found.' : 'لم يتم العثور على منتجات.')
                    ])
                ]
            ])
        ]);
    }
};
